import React from "react";
import moment from "moment";
import { connect, type ConnectedProps } from "react-redux";
import { map, orderBy, some, isEmpty, filter, slice } from "lodash";

import classnames from "classnames";

import Button from "components/Button";
import ConfirmAchCancellationModal from "pages/dashboards/individualDashboard/dashboard/ConfirmAchCancellationModal";
import "./IndividualContributions.scss";
import { paymentStatusToEnglish } from "statics/statusToEnglishMapping";
import type { RootState } from "types/state";

const achDepositCategory = "IRA_CONTRIBUTION_ADHOC";

const mapStateToProps = (state: RootState) => {
  const pendingAchTransactions = filter(
    state.transactions.transactions,
    (t) =>
      t.subCategory === achDepositCategory &&
      (t.status === "PENDING" || t.status === "HOLD")
  );
  const descendingTransactions = orderBy(
    pendingAchTransactions,
    "createdAt",
    "desc"
  );
  return {
    transactions: descendingTransactions,
  };
};

const connector = connect(mapStateToProps, null);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  isEmployer?: boolean;
  contributionInfo?: {
    amount?: number;
    end?: string;
    schedule?: string;
    scheduleId?: string;
    frequency?: string;
  };
}

type Props = OwnProps & PropsFromRedux;

interface State {
  showConfirmationModal: boolean;
  pendingCancellationId: string | null;
}

export class OneTimeContributionCard extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      showConfirmationModal: false,
      pendingCancellationId: null,
    };
  }

  _buildTransactionRow = () => {
    return map(this.props.transactions, (transaction) => {
      const createdAt = moment(transaction.createdAt).format("MMM Do YYYY");
      let action = null;
      const cancellationPending =
        transaction.cancellationStatus === "SUBMITTED";
      if (
        transaction.status === "PENDING" &&
        !cancellationPending &&
        transaction.subCategory === achDepositCategory
      ) {
        action = (
          <>
            <span className="remove-bank">
              <Button
                btnLabel="cancel"
                name="cancel"
                color="red"
                size="sm"
                onClick={() => this._openConfirmModal(transaction.id)}
              />
            </span>
          </>
        );
      }

      let status =
        (paymentStatusToEnglish as Record<string, string>)[
          transaction.status as string
        ] || transaction.status;
      status = cancellationPending ? "Cancellation pending" : status;

      const oneTimeContributionAmount = slice(transaction.memo, 30);
      return (
        <div className="one-time-contribution-container" key={transaction.id}>
          <div
            className={classnames("contribution-source-container", {
              "bank-source": !this.props.isEmployer,
            })}
          >
            <div
              className={classnames("contribution-info", {
                ["no-contribution"]: isEmpty(this.props.contributionInfo),
              })}
            >
              <div className="contribution-heading">
                <h3 className="contribution">One-Time Contribution</h3>
                <div className="contribution-schedule">
                  <span className="contribution-status">Status:</span>
                  <span className="contribution-status"> {status}</span>
                </div>
              </div>
              <div className="contribution-amount-and-actions">
                <span className="contribution-amount">
                  {oneTimeContributionAmount}
                </span>
              </div>
              <div className="flex-break" />
              <div className="menu-and-date">
                <div className="next-run-date">
                  <span className="dot" />
                  <span>Contribution Date: {createdAt}</span>
                </div>

                <div className="menu-btn">
                  <div>{action}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    });
  };

  _openConfirmModal = (pendingCancellationId: string) => {
    this.setState({
      pendingCancellationId,
      showConfirmationModal: true,
    });
  };

  _closeConfirmModal = () => {
    this.setState({
      showConfirmationModal: false,
      pendingCancellationId: null,
    });
  };

  _transactionsContainPendingAchDeposit = () => {
    return some(
      this.props.transactions,
      (t) =>
        t.subCategory === achDepositCategory &&
        t.status === "PENDING" &&
        t.cancellationStatus !== "SUBMITTED"
    );
  };

  render() {
    const containsPendingAch = this._transactionsContainPendingAchDeposit();
    // PRESERVED BUG (conversion-only): _buildTransactionRow takes no args; the
    // original passed containsPendingAch and the value is ignored at runtime.
    // @ts-expect-error preserve original extra-argument call verbatim
    const transactionRow = this._buildTransactionRow(containsPendingAch);

    return (
      <div className="help">
        {this.state.showConfirmationModal && (
          <ConfirmAchCancellationModal
            onClose={this._closeConfirmModal}
            transactionId={this.state.pendingCancellationId as string}
          />
        )}
        <div>{containsPendingAch}</div>
        <div>{transactionRow}</div>
      </div>
    );
  }
}

export default connector(OneTimeContributionCard);
